import { Loader2, AlertCircle, Phone } from 'lucide-react';
import { Badge } from './badge';
import { usePhoneOperator } from '@/hooks/usePhoneOperator';
import { cn } from '@/lib/utils';
import { getOperadoraColor, formatOperadora } from '@/lib/operadoras';
import { formatPhone } from '@/utils/phone';

interface PhoneWithOperatorProps {
  phone: string | undefined | null;
  variant?: 'inline' | 'badge-only' | 'readonly';
  className?: string;
  showIcon?: boolean;
}

/**
 * Componente que exibe telefone com badge da operadora
 * Consulta automaticamente via API com cache de 24h
 */
export function PhoneWithOperator({
  phone,
  variant = 'inline',
  className,
  showIcon = false,
}: PhoneWithOperatorProps) {
  const { operadora, isLoading, error } = usePhoneOperator(phone);

  const getOperatorBadge = () => {
    if (isLoading) {
      return (
        <Badge variant="secondary" className="px-1 py-0 text-[9px] flex items-center gap-1 opacity-60">
          <Loader2 className="h-2.5 w-2.5 animate-spin" />
          <span className="text-muted-foreground">...</span>
        </Badge>
      );
    }

    if (error) {
      return (
        <Badge variant="secondary" className="px-1 py-0 text-[9px] flex items-center gap-1 opacity-40">
          <AlertCircle className="h-2.5 w-2.5" />
          <span className="text-muted-foreground">N/D</span>
        </Badge>
      );
    }

    // Badge para telefone FIXO
    if (operadora === 'FIXO') {
      return (
        <Badge 
          variant="outline" 
          className="px-1 py-0 text-[9px] bg-muted/30 text-muted-foreground border-border/50 font-normal opacity-70"
        >
          <span>Fixo</span>
        </Badge>
      );
    }

    if (operadora) {
      return (
        <Badge 
          variant="outline" 
          className={cn(
            "px-1 py-0 text-[9px] font-normal opacity-75",
            getOperadoraColor(operadora)
          )}
        >
          <span>{formatOperadora(operadora)}</span>
        </Badge>
      );
    }

    return null;
  };

  if (variant === 'badge-only') {
    const badge = getOperatorBadge();
    if (!badge) return null;
    return badge;
  }

  if (variant === 'readonly') {
    return (
      <div className={cn('flex items-center gap-2', className)}>
        {showIcon && <Phone className="h-4 w-4 text-muted-foreground" />}
        <span className="text-xs">{formatPhone(phone)}</span>
        {getOperatorBadge()}
      </div>
    );
  }

  // variant === 'inline'
  return (
    <div className={cn('flex items-center gap-2', className)}>
      <span className="text-xs font-medium">{formatPhone(phone)}</span>
      {getOperatorBadge()}
    </div>
  );
}
